// Copyright ©️ 2026 eVolve MEP, LLC

import type {
  BendValidationFailure,
  BendValidationRequirement,
  BendValidationStatus,
  MachineElement,
} from '@evolvemep/api-ts';
import { Card, DataList, Group, type MantineColor, Stack, Text, ThemeIcon } from '@mantine/core';

import { EvolveIcon, type EvolveIconName } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { convertInchesToImperialString } from 'helpers/convertInchesToImperialString';
import { isNil } from 'helpers/isNotNil';

const statusDisplays: Record<
  BendValidationStatus,
  {
    label: string;
    icon: EvolveIconName;
    color: MantineColor;
  }
> = {
  // Black in the light scheme, white on the Machine Control stage
  Pass: { label: 'Pass', icon: 'Check', color: 'var(--mantine-color-bright)' },
  Fail: { label: 'Fail', icon: 'Alert', color: 'red' },
  NotApplicable: { label: 'N/A', icon: 'Info', color: 'gray' },
};

const requirements: { requirement: BendValidationRequirement; label: string }[] = [
  { requirement: 'MinimumFrontStraightLength', label: 'Min. front straight' },
  { requirement: 'MinimumStraightBetweenBends', label: 'Min. straight between bends' },
  { requirement: 'MinimumFinalStraightLength', label: 'Min. final straight' },
];

const formatFeet = (valueInFeet: number) => convertInchesToImperialString(valueInFeet * 12);

const RequirementRow = ({ label, failure }: { label: string; failure?: BendValidationFailure }) => (
  <DataList.Item
    px={6}
    styles={{
      item: {
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--mantine-spacing-xl)',
      },
    }}
  >
    <DataList.ItemLabel fz="sm" c={isNil(failure) ? 'green.8' : 'red.8'}>
      {isNil(failure) ? label : `${label} (≥${formatFeet(failure.minimum)})`}
    </DataList.ItemLabel>
    <DataList.ItemValue
      display="flex"
      style={{
        alignItems: 'center',
      }}
    >
      {isNil(failure) ? (
        <ThemeIcon size="xs" radius="xl" color="green">
          <EvolveIcon icon="Check" size="sm" />
        </ThemeIcon>
      ) : (
        <Text fz="sm" c="red.8">
          {formatFeet(failure.actual)}
        </Text>
      )}
    </DataList.ItemValue>
  </DataList.Item>
);

type Props = {
  element: MachineElement;
};

/**
 * The Validation column's cell: a bend's pass/fail status, with a tooltip breaking out each machine
 * minimum it was measured against and the values behind any that it missed.
 */
export const BendValidationCell = ({ element: { bendValidationStatus, bendValidationErrors } }: Props) => {
  const { label, icon, color } = statusDisplays[bendValidationStatus];
  return (
    <Group gap="xs" wrap="nowrap">
      <AgGridStyleTooltip
        maw={500}
        label={
          bendValidationStatus === 'NotApplicable' ? (
            'No bend validation result is available.'
          ) : (
            <Stack gap="xs" ta="left" py={6}>
              <Text fw={600}>Bend Validation</Text>
              <Card p={0} withBorder bg="gray.0" py={6}>
                <DataList orientation="horizontal" withDivider gap={6}>
                  {requirements.map(({ requirement, label: requirementLabel }) => (
                    <RequirementRow
                      key={requirement}
                      label={requirementLabel}
                      failure={bendValidationErrors.find((failure) => failure.requirement === requirement)}
                    />
                  ))}
                </DataList>
              </Card>
            </Stack>
          )
        }
        openDelay={0}
        withArrow
        openOnTap
      >
        <EvolveIcon icon={icon} inline color={color} size="sm" onClick={(e) => e.stopPropagation()} />
      </AgGridStyleTooltip>
      <Text c={color}>{label}</Text>
    </Group>
  );
};
